import { LoadingButton } from "@mui/lab";
import { Alert, Box, Breadcrumbs, Button, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { useDispatch } from "react-redux";
import { SelectBoxOption, TagInfo } from "../../interfaces";
import { ITask, useCreateTaskMutation, useUpdateTaskMutation } from "../../redux/services";
import { closeDialog } from "../../redux/slices/dialog";
import { TAG_CANONICAL_NAMES } from "../../utils";
import { TagsHighlightSelect } from "../tagsDropdown";
import { taskHistoryInterface } from "../../screens/viewTask/subTaskTree";
import { NavigateNext } from "@mui/icons-material";

export const AddEditTask: React.FC<{
    children?: JSX.Element,
    isEdit: boolean,
    data?: ITask,
    parentObjectId: string,
    parentObjectType: string,
    parentTask?: ITask,
    refetch: any,
    hideTags?: boolean,
    subTaskParents?: taskHistoryInterface[],
}> = (props) => {

    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [createTask, createdTaskData] = useCreateTaskMutation();
    const [updateTask, updatedTaskData] = useUpdateTaskMutation();

    const [state, setState] = React.useState<{
        name: string,
        description?: string,
        taskCategory: SelectBoxOption | null,
        taskType: SelectBoxOption | null,
        taskPriorty: SelectBoxOption | null,
        labels: SelectBoxOption[] | null,
    }>({ name: "", description: "", taskCategory: null, taskType: null, taskPriorty: null, labels: [] });

    const [error, setError] = React.useState({ name: false });

    const updateState = (key: keyof typeof state, value: string) => {
        setState({ ...state, [key]: value });
        setError({ ...error, [key]: !value?.trim()?.length })
    };

    const updateDropDown = (key: keyof typeof state, data: SelectBoxOption) => {
        setState({ ...state, [key]: data });
    };

    const onCancelBtnClicked = () => {
        dispatch(closeDialog());
    };

    const onCreateUpdateBtnClicked = async () => {

        let parent = {
            object_id: props.parentObjectId,
            object_type: props.parentObjectType
        };

        let tags_info: Partial<TagInfo>[] = [state.taskCategory, state.taskType, state.taskPriorty, ...(state?.labels ?? [])].filter((t) => t?.value) as unknown as TagInfo[];

        if (tags_info.length > 0) {
            tags_info = tags_info.map((tag) => ({
                tag_group_id: tag.tag_group_id as string,
                tag_id: tag._id as string || tag.tag_id as string
            }));
        }

        if (state.name.trim().length) {
            if (props.isEdit) {
                const res: any = await updateTask({ payload: { name: state.name, description: state.description, parent, tags_info: props.hideTags ? undefined : tags_info }, id: props.data?._id ?? "" })

                if (res.data) {
                    enqueueSnackbar("Task Updated Successfully", { variant: "success" });
                    props.refetch();
                    dispatch(closeDialog());
                } else {
                    enqueueSnackbar(res.error?.data?.title ?? "Oops! Something went wrong, Unable to create Task", { variant: "error" })
                }
            } else {
                const res: any = await createTask({ name: state.name, description: state.description, parent, tags_info });

                if (res.data) {
                    enqueueSnackbar("Task Created Successfully", { variant: "success" });
                    props.refetch();
                    dispatch(closeDialog());
                } else {
                    enqueueSnackbar(res.error?.data?.title ?? "Oops! Something went wrong, Unable to create Task", { variant: "error" })
                }
            }
        } else {
            setError({
                ...error,
                name: !state.name.trim().length
            })
        }
    };

    const giveMeButtonName = () => {
        return `${props.isEdit ? "Done" : "Create"}`;
    };

    const giveMeInfoText = () => {
        if (props.isEdit && updatedTaskData.isError) {
            return <Alert severity="error">Oops! Something went wrong, Unable to update the Task. Try Again Later!</Alert>
        }
        if (createdTaskData.isError) {
            return <Alert severity="error">Oops! Something went wrong, Unable to create the Task. Try Again Later!</Alert>
        }
    };

    React.useEffect(() => {
        if (props.isEdit && props.data?._id) {

            let filteredTaskCategory: SelectBoxOption | null = null;
            let filteredTaskPriority: SelectBoxOption | null = null;
            let filteredTaskType: SelectBoxOption | null = null;
            let filteredLabel: SelectBoxOption[] | null = [];

            if (!props.hideTags) {
                props.data?.tags_info?.forEach(tag => {

                    const _tag: SelectBoxOption = { ...tag, value: `${tag._id}`, label: `${tag.tag_name}` };

                    if (tag.tag_group_canonical_name === TAG_CANONICAL_NAMES.task_category) {
                        filteredTaskCategory = _tag;
                    };

                    if (tag.tag_group_canonical_name === TAG_CANONICAL_NAMES.task_priority) {
                        filteredTaskPriority = _tag;
                    };

                    if (tag.tag_group_canonical_name === TAG_CANONICAL_NAMES.task_type) {
                        filteredTaskType = _tag
                    };

                    if (tag.tag_group_canonical_name === TAG_CANONICAL_NAMES.label) {
                        filteredLabel?.push(_tag);
                    };

                })
            };

            setState({
                ...state,
                name: props.data.name,
                description: props.data.description,
                taskCategory: filteredTaskCategory,
                taskType: filteredTaskType,
                taskPriorty: filteredTaskPriority,
                labels: filteredLabel
            });
        }
        // eslint-disable-next-line
    }, [props.isEdit]);

    return (
        (<Box>

            {/* Breadscrum */}
            {props.subTaskParents && <Breadcrumbs sx={{ mb: 3 }} separator={<NavigateNext fontSize="small" />}>
                {props.subTaskParents?.map((his, index) => {
                    return <Typography key={index} variant="body1">
                        {his.label}
                    </Typography>
                })}

                {props.parentTask && <Typography variant="body1">
                    {props.parentTask?.sequence_id ?? ""}
                </Typography>}

                <Typography variant="body1" color="primary">
                    {props.isEdit ? `Edit` : `New`}
                </Typography>
            </Breadcrumbs>}


            {/* Fields */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
                {/* Name */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label={`Task Name (${state.name.length}/100)`}
                        value={state.name}
                        required={true}
                        disabled={false}
                        error={error.name}
                        helperText={error.name ? "Please enter the task name" : ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.value.length <= 100) {
                                updateState("name", e.target.value)
                            }
                        }}
                    />
                </Grid>

                {!props.hideTags && <>
                    {/* Task Type */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TagsHighlightSelect
                            id="task-type"
                            margin="none"
                            size="medium"
                            value={state.taskType}
                            onChange={(data: SelectBoxOption) => updateDropDown("taskType", data)}
                            canonical_name={TAG_CANONICAL_NAMES.task_type}
                        />
                    </Grid>

                    {/* Task Category */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TagsHighlightSelect
                            id="task-category"
                            margin="none"
                            size="medium"
                            value={state.taskCategory}
                            onChange={(data: SelectBoxOption) => updateDropDown("taskCategory", data)}
                            canonical_name={TAG_CANONICAL_NAMES.task_category}
                        />
                    </Grid>

                    {/* Task Priority */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TagsHighlightSelect
                            id="task-priority"
                            margin="none"
                            size="medium"
                            value={state.taskPriorty}
                            onChange={(data: SelectBoxOption) => updateDropDown("taskPriorty", data)}
                            canonical_name={TAG_CANONICAL_NAMES.task_priority}
                        />
                    </Grid>

                    {/* Labels */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TagsHighlightSelect
                            id="task-labels"
                            margin="none"
                            size="medium"
                            multiple
                            value={state.labels}
                            onChange={(data: SelectBoxOption) => updateDropDown("labels", data)}
                            canonical_name={TAG_CANONICAL_NAMES.label}
                        />
                    </Grid>
                </>}

                {/* Description */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={state.description}
                        multiline
                        rows={5}
                        label={`Description (${state.description?.length ?? 0}/200)`}
                        placeholder={"Please enter the description of the task"}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.value.length <= 200) {
                                updateState("description", e.target.value)
                            }
                        }}
                    />
                </Grid>

                {/* Info Text */}
                <Grid size={12}>
                    {giveMeInfoText()}
                </Grid>

                {/* Create/Update Action */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        onClick={onCreateUpdateBtnClicked}
                        loading={createdTaskData.isLoading || updatedTaskData.isLoading}
                    >
                        {giveMeButtonName()}
                    </LoadingButton>
                </Grid>

                {/* Cancel Btn */}
                <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6, xl: 6 }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={onCancelBtnClicked}
                        disabled={false}
                    >
                        {"Cancel"}
                    </Button>
                </Grid>
            </Grid>
        </Box>)
    );
}